import { type KeyboardEvent } from 'react';

export type InventoryLocationFilterOption = {
  id: string;
  label: string;
};

type InventoryLocationFilterRowProps = {
  options: InventoryLocationFilterOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  ariaLabel: string;
  ariaDescribedBy?: string;
  className?: string;
};

const resolveLocationChipClassName = (selected: boolean): string => {
  if (selected) {
    return 'shrink-0 rounded-full border border-primary-600 bg-primary-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/45';
  }
  return 'shrink-0 rounded-full border border-gray-200/90 bg-white px-4 py-2 text-sm font-bold text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800';
};

export const InventoryLocationFilterRow = ({
  options,
  selectedIndex,
  onSelect,
  ariaLabel,
  ariaDescribedBy,
  className = '',
}: InventoryLocationFilterRowProps) => {
  const handleChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onSelect(index);
  };

  const wrapperClassName = ['flex flex-nowrap gap-2 overflow-x-auto pt-0.5 pb-1', className].filter(Boolean).join(' ');

  const resolveChipEdgeClassName = (index: number) => {
    const isFirst = index === 0;
    const isLast = index === options.length - 1;
    return [isFirst ? 'ml-1' : '', isLast ? 'mr-1' : ''].filter(Boolean).join(' ');
  };

  return (
    <div className={wrapperClassName} role="tablist" aria-label={ariaLabel} aria-describedby={ariaDescribedBy}>
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        const edgeClassName = resolveChipEdgeClassName(index);
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(index)}
            onKeyDown={(event) => handleChipKeyDown(event, index)}
            className={`${resolveLocationChipClassName(selected)} ${edgeClassName}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
